import { Component, CSSProperties } from 'react';
import { Navigate, NavLink } from 'react-router-dom';
import MonacoEditor from 'react-monaco-editor';
import * as Message from '../../common/js/message';
import { Request } from '../../common/js/request';
import { Context, AppContext } from "../../common/js/context";
import { T, COMMON } from '../../common/js/i18n';
import { renderCode, exportTemplate } from "../common";
import { getUserState } from '../../store/index';
import ModalDialog from "../../components/ModalDialog";
import iconCircleQuestion from '../../assets/icon/circle-question.svg';
import iconMaximize from '../../assets/icon/maximize.svg';
import iconMinimize from '../../assets/icon/minimize.svg';
import styles from './Detail.module.css';
import DataProcessorEdit from "../DataProcessorEdit";
import DataHelp from "../help/DataHelp";
import ClientDownload from "../help/ClientDownload";
import Login from "../Login";

enum CodeType {
    Data = "Data",
    Template = "Template",
}

interface TemplateDetail {
    id: string;
    name: string;
    content: string;
    created_time: string;
    last_modified_time: string;
}

interface SchemeDetail {
    id: string;
    name: string;
    demo_data: string;
    data_processor?: string;
    created_time: string;
    last_modified_time: string;
    templates: TemplateDetail[];
}

interface Props {
    schemeId: string;
}

interface Template {
    id?: string;
    name: string;
    content: string;
    export_path?: string;
}

interface State {
    inited: boolean;
    cloneDone: boolean;
    codeType?: CodeType;
    name: string;
    data: string;
    data_processor?: string;
    templates: Template[];
    activeTemplateIndex: number;
    code: string;
    processorDetailActive: boolean;
    dataHelpActive: boolean;
    loginActive: boolean;
    downloadActive: boolean;
}

export default class Detail extends Component<Props, State> {
    static contextType = Context;
    constructor(props: Props, context: AppContext) {
        super(props);
        this.state = {
            inited: false,
            cloneDone: false,
            codeType: undefined,
            name: "",
            data: "{}",
            data_processor: "",
            templates: [{
                name: { en: "Demo scheme", zh: "Demo方案" }[context.lang],
                content: ""
            }],
            activeTemplateIndex: 0,
            code: "",
            processorDetailActive: false,
            dataHelpActive: false,
            loginActive: false,
            downloadActive: false,
        };
    }
    componentDidMount() {
        this.readDetail(this.props.schemeId)
    }
    componentWillReceiveProps(nextProps: Props) {
        this.readDetail(nextProps.schemeId)
    }
    async readDetail(schemeId: string) {
        const context: AppContext = this.context as any;
        let scheme: SchemeDetail = await new Request<{
            id: string;
        }, SchemeDetail>("/api/scheme/readSharedScheme").call({
            id: schemeId
        });
        let templates: Template[] = scheme.templates;
        if (!templates.length) {
            templates = [{
                name: { en: "Demo scheme", zh: "Demo方案" }[context.lang],
                content: ""
            }];
        }
        this.setState({
            inited: true,
            name: scheme.name,
            data: scheme.demo_data,
            data_processor: scheme.data_processor,
            templates: templates,
            activeTemplateIndex: 0
        });
        renderCode(templates[0].content, scheme.demo_data, (code) => {
            this.setState({
                code: code
            });
        }, scheme.data_processor);
    }
    async cloneTemplate() {
        let userState = getUserState();
        if (userState.logined) {
            await new Request<{
                id: string;
            }, {
                id: string;
            }>("/api/scheme/cloneSharedScheme").call({
                id: this.props.schemeId
            });
            this.setState({
                cloneDone: true
            });
        } else {
            this.setState({
                loginActive: true
            });
        }
    }
    cloneTemplates(): Template[] {
        return this.state.templates.map((template) => {
            return {
                id: template.id,
                name: template.name,
                content: template.content,
            }
        });
    }
    async exportTemplate() {
        const context: AppContext = this.context as any;
        if (!(window as any).__TAURI__) {
            this.setState({
                downloadActive: true
            });
            return;
        }
        let data = this.state.data;
        let data_processor = this.state.data_processor;
        let templates = [];
        for (let template of this.state.templates) {
            let content = template.content;
            let export_path_template = template.export_path;
            let export_path: string | undefined = undefined;
            if (export_path_template) {
                let template: string = export_path_template;
                export_path = await new Promise((resolve, reject) => {
                    renderCode(template, data, resolve, data_processor);
                });
            }
            let code: string = await new Promise((resolve, reject) => {
                renderCode(content, data, resolve, data_processor);
            });
            templates.push({
                code: code,
                export_path: export_path
            });
        }
        let done = await exportTemplate(templates);
        if (done) {
            Message.success({ en: "Export successfully", zh: "导出成功" }[context.lang]);
        }
    }
    render() {
        if (this.state.cloneDone) {
            return (
                <Navigate to="/dashboard" />
            );
        }
        let titleStyle: CSSProperties = { marginTop: "0.5em", marginBottom: "0.5em" };
        let panelStyle: CSSProperties = { height: "100%", display: "flex", flexDirection: "column" };
        let containerStyle: CSSProperties = { flex: "1" };
        const renderDataEditor = () => {
            let resizeListener: any = undefined;
            return (
                <div style={panelStyle}>
                    <h4 style={titleStyle}>
                        {T({ en: "Data", zh: "数据" })}:
                        <img src={iconCircleQuestion} alt="" onClick={() => {
                            this.setState({
                                dataHelpActive: true
                            });
                        }} className={styles['action-icon']} style={{ marginLeft: "0.5em" }} />
                        <div style={{ float: "right" }}>
                            <button type="button" className="btn" onClick={() => {
                                this.setState({
                                    processorDetailActive: true
                                });
                            }} style={{ marginRight: '1em' }}>{T({ en: "Data pre-processor", zh: "数据预处理器" })}</button>
                            {
                                CodeType.Data === this.state.codeType &&
                                <img src={iconMinimize} alt="" onClick={() => {
                                    this.setState({
                                        codeType: undefined
                                    });
                                }} className={styles['action-icon']} />
                            }
                            {
                                !this.state.codeType &&
                                <img src={iconMaximize} alt="" onClick={() => {
                                    this.setState({
                                        codeType: CodeType.Data
                                    });
                                }} className={styles['action-icon']} />
                            }
                        </div>
                    </h4>
                    <div style={containerStyle}>
                        <MonacoEditor
                            width="100%"
                            height="100%"
                            language="json"
                            theme="vs-dark"
                            value={this.state.data}
                            options={{
                                selectOnLineNumbers: true
                            }}
                            onChange={(newVal) => {
                                this.setState({
                                    data: newVal
                                });
                                renderCode(this.state.templates[this.state.activeTemplateIndex].content, newVal, (code) => {
                                    this.setState({
                                        code: code
                                    });
                                }, this.state.data_processor);
                            }}
                            editorDidMount={(editor, monaco) => {
                                if (!resizeListener) {
                                    resizeListener = () => {
                                        editor.layout();
                                    };
                                    window.addEventListener("resize", resizeListener);
                                }
                            }}
                            editorWillUnmount={() => {
                                if (resizeListener) {
                                    window.removeEventListener("resize", resizeListener);
                                    resizeListener = undefined;
                                }
                            }}
                        />
                    </div>
                </div>
            );
        };

        const renderTemplateEditor = () => {
            let resizeListener: any = undefined;
            return (
                <div style={panelStyle}>
                    <h4 style={titleStyle}>
                        {T({ en: "Template", zh: "模板" })}:
                    </h4>
                    <div style={containerStyle}>
                        <MonacoEditor
                            width="100%"
                            height="100%"
                            language="lua"
                            theme="vs-dark"
                            value={this.state.templates[this.state.activeTemplateIndex].content}
                            options={{
                                selectOnLineNumbers: true,
                                readOnly: true
                            }}
                            editorDidMount={(editor, monaco) => {
                                if (!resizeListener) {
                                    resizeListener = () => {
                                        editor.layout();
                                    };
                                    window.addEventListener("resize", resizeListener);
                                }
                            }}
                            editorWillUnmount={() => {
                                if (resizeListener) {
                                    window.removeEventListener("resize", resizeListener);
                                    resizeListener = undefined;
                                }
                            }}
                        />
                    </div>
                </div>
            );
        };

        const renderTemplateTab = (item: Template, index: number) => {
            let active = this.state.activeTemplateIndex === index;
            let style: CSSProperties = {
                cursor: "pointer",
                padding: "0.25em 1.5em"
            };
            if (active) {
                style.color = "rgb(25, 118, 210)";
                style.borderBottom = "2px solid";
                style.cursor = "default";
            }
            return (
                <li key={index} style={{ display: "inline-block", position: "relative", ...style }} onClick={() => {
                    this.setState({
                        activeTemplateIndex: index
                    });
                    renderCode(item.content, this.state.data, (code) => {
                        this.setState({
                            code: code
                        });
                    }, this.state.data_processor);
                }}>
                    {item.name}
                </li>
            )
        };
        if (this.state.inited) {
            let resizeListener: any = undefined;
            let topHeight = "2em";
            const context: AppContext = this.context as any;
            return (
                <div style={{ height: "100%", position: "relative" }}>
                    <div style={{ height: topHeight, position: "absolute", top: 0, left: 0, right: 0, boxSizing: "border-box", borderBottom: "1px solid #CCC", display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: "0.5em" }}>
                        <span>
                            <span><span style={{ fontWeight: "bold" }}>{T({ en: "Scheme name", zh: "方案名称" })}:</span>{this.state.name}</span>
                            <button type="button" className="btn" onClick={this.cloneTemplate.bind(this)}>{T({ en: "Copy this scheme", zh: "复制该方案" })}</button>
                        </span>
                        <span>
                            <NavLink to="/">{T({ en: "Home", zh: "主页" })}</NavLink>
                        </span>
                    </div>
                    <div style={{ position: "absolute", top: topHeight, bottom: 0, left: 0, right: 0, overflow: "auto" }}>
                        {
                            CodeType.Data === this.state.codeType && renderDataEditor()
                        }
                        {
                            CodeType.Template === this.state.codeType && renderTemplateEditor()
                        }
                        {
                            !this.state.codeType &&
                            <>
                                <div style={{ width: "50%", height: "100%", display: "inline-block", boxSizing: "border-box", paddingRight: "0.5em", float: "left" }}>
                                    <div style={{ height: "30%" }}>
                                        {
                                            renderDataEditor()
                                        }
                                    </div>
                                    <div style={{ height: "70%", display: "flex", flexDirection: "column" }}>
                                        <div style={{ flexGrow: 0, display: "flex", justifyContent: "space-between" }}>
                                            <ul className={styles['tmpl-tab-ul']}>
                                                {
                                                    this.state.templates.map(renderTemplateTab)
                                                }
                                            </ul>
                                        </div>
                                        <div style={{ flexGrow: 1 }}>
                                            {
                                                renderTemplateEditor()
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div style={{ width: "50%", height: "100%", display: "inline-block", boxSizing: "border-box", paddingLeft: "0.5em", float: "right" }}>
                                    <div style={panelStyle}>
                                        <h4 style={titleStyle}>
                                            {T({ en: "Code", zh: "代码" })}:
                                            <button type="button" className="btn" onClick={this.exportTemplate.bind(this)} style={{ float: "right", marginRight: "0.5em" }}>{T({ en: "Export code", zh: "导出代码" })}</button>
                                        </h4>
                                        <div style={containerStyle}>
                                            <MonacoEditor
                                                width="100%"
                                                height="100%"
                                                language="javascript"
                                                theme="vs-dark"
                                                value={this.state.code}
                                                options={{
                                                    selectOnLineNumbers: true,
                                                    readOnly: true
                                                }}
                                                editorDidMount={(editor, monaco) => {
                                                    if (!resizeListener) {
                                                        resizeListener = () => {
                                                            editor.layout();
                                                        };
                                                        window.addEventListener("resize", resizeListener);
                                                    }
                                                }}
                                                editorWillUnmount={() => {
                                                    if (resizeListener) {
                                                        window.removeEventListener("resize", resizeListener);
                                                        resizeListener = undefined;
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                        {
                            this.state.processorDetailActive && (
                                <ModalDialog title={{ en: "Data pre-processor", zh: "数据预处理器" }[context.lang]} closable={false} centerStyle={{
                                    width: '80%',
                                    height: '80%',
                                }} dialogStyle={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }} contentStyle={{
                                    flex: 1,
                                }}>
                                    <DataProcessorEdit value={this.state.data_processor || ''} readOnly={true} onClose={() => {
                                        this.setState({
                                            processorDetailActive: false
                                        });
                                    }} />
                                </ModalDialog>
                            )
                        }
                        {
                            this.state.dataHelpActive && (
                                <ModalDialog title={{ en: "Help of writing data", zh: "数据编辑帮助" }[context.lang]} closable={false}>
                                    <DataHelp onClose={() => {
                                        this.setState({
                                            dataHelpActive: false
                                        });
                                    }} />
                                </ModalDialog>
                            )
                        }
                    </div>
                    {
                        this.state.loginActive && (
                            <ModalDialog title={COMMON.TIP_TITLE[context.lang]} closable={true} onClose={() => {
                                this.setState({
                                    loginActive: false
                                });
                            }}>
                                <div style={{ padding: "0.5em" }}>
                                    <p style={{ margin: "0.5em 0" }}>
                                        {T({
                                            en: "You are a guest now, you can copy this scheme after login.",
                                            zh: "您尚未登录，登录之后才可以复制。"
                                        })}
                                    </p>
                                    <Login onDone={() => {
                                        this.setState({
                                            loginActive: false
                                        });
                                        this.cloneTemplate();
                                    }} />
                                </div>
                            </ModalDialog>
                        )
                    }
                    {
                        this.state.downloadActive && (
                            <ModalDialog title={COMMON.TIP_TITLE[context.lang]} closable={false}>
                                <ClientDownload onClose={() => {
                                    this.setState({
                                        downloadActive: false
                                    });
                                }} />
                            </ModalDialog>
                        )
                    }
                </div>
            );
        } else {
            return (<div></div>);
        }
    }
}
